/**
 * LandingI18nProvider — locale source for the public landing page.
 *
 * v10-A4: the landing lives OUTSIDE the app's auth-gated I18nContext (which
 * reads the logged-in profile). Landing locale comes from the URL instead:
 *   1. ?lang=ko|zh|ja|... explicit (blog/SEO links set it)
 *   2. ?ref= hints (ref contains a known locale code, e.g. ref=naver_ko)
 *   3. navigator.languages — the one true signal for "made for you" (N1)
 *   4. English fallback (getStrings default)
 *
 * Landing-only string keys live under `landing.*` in shared/src/i18n/locales/
 * so all seven locale files stay the single translation surface.
 *
 * Hooks/context live in landing-locale.ts (this file exports only the
 * component — react-refresh/only-export-components).
 */

import { useMemo, type ReactNode } from 'react'
import { LandingLocaleContext } from './landing-locale'

const SUPPORTED = ['ko', 'zh', 'zh-TW', 'ja', 'es', 'fr', 'de'] as const
type Supported = (typeof SUPPORTED)[number]

const REF_HINTS: Record<string, Supported> = {
  naver_ko: 'ko',
  cafe_ko: 'ko',
  dcard: 'zh',
  ptt: 'zh',
  threads_tw: 'zh',
  fivech_ja: 'ja',
  x_ja: 'ja',
}

function detectLocale(): string {
  if (typeof window === 'undefined') return 'en'

  // 0. Locale-prefix path (/ko/…, /zh-TW/…) — the indexable URLs
  //    (multilingual-seo.md M2). Highest priority: the URL is the promise.
  const prefixed = window.location.pathname.match(/^\/(ko|zh-TW|ja)(?=\/|$)/)
  if (prefixed) {
    console.log('[LandingI18n] detected locale from pathname:', prefixed[1], 'pathname:', window.location.pathname)
    return prefixed[1]
  }

  // 0.5 Prerendered shell handoff (set before React mounts).
  const shell = (window as unknown as { __PAGE_LOCALE__?: string }).__PAGE_LOCALE__
  if (typeof shell === 'string' && shell && shell !== 'en') {
    console.log('[LandingI18n] detected locale from shell:', shell)
    return shell
  }

  const params = new URLSearchParams(window.location.search)
  // The extension's install-welcome emits BCP-47-with-underscore codes
  // (zh_CN / zh_TW) and visitors send region tags (ko-KR, zh-Hant…).
  // Normalize both down to our hyphen-based table keys.
  const lang = params.get('lang')?.replace('_', '-')
  if (lang === 'zh-TW' || lang === 'zh-Hant') return 'zh-TW'
  if (lang) {
    const base = lang.split('-')[0]
    // Any other zh region (zh-CN, zh-Hans, zh-HK-…handled above) → zh table
    if (base === 'zh') return 'zh'
    if ((SUPPORTED as readonly string[]).includes(base)) return base
  }

  const ref = params.get('ref')
  if (ref && REF_HINTS[ref]) return REF_HINTS[ref]

  for (const tag of navigator.languages ?? []) {
    const base = tag.split('-')[0].toLowerCase()
    if (base === 'zh') return /tw|hant|hk/i.test(tag) ? 'zh-TW' : 'zh'
    if ((SUPPORTED as readonly string[]).includes(base)) return base
  }
  console.log('[LandingI18n] falling back to en, pathname:', window.location.pathname)
  return 'en'
}

export function LandingI18nProvider({ children }: { children: ReactNode }) {
  const locale = useMemo(() => detectLocale(), [])
  return (
    <LandingLocaleContext.Provider value={locale}>
      {children}
    </LandingLocaleContext.Provider>
  )
}
